import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';

const PAYMENT_CONTACT = {
  name: 'Mohammad Rassedu',
  email: 'info@mrmlettings.co.uk',
  phone: '01642 243 378',
  location: 'Middlesbrough, UK',
};

const BANK_DETAILS = {
  accountName: 'MRM Lettings Property and Management LTD',
  sortCode: '20-83-73',
  accountNumber: '13319601',
};

const instructions = [
  'Use bank transfer (BACS or Faster Payments). Card payments are not processed online.',
  'Quote your tenancy address or reference on every payment so we can reconcile it quickly.',
  'Send proof of payment by email if you need a receipt on the same business day.',
  'Allow one working day for funds to reflect on your tenancy ledger.',
];

function CopyableDetail({ label, value }) {
  const copy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value);
    }
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Box>
        <Typography variant="caption" color="text.secondary" textTransform="uppercase">
          {label}
        </Typography>
        <Typography variant="h6" fontWeight={700}>
          {value}
        </Typography>
      </Box>
      <IconButtonBase onClick={copy} aria-label={`Copy ${label}`}>
        <ContentCopyIcon fontSize="small" />
      </IconButtonBase>
    </Stack>
  );
}

function IconButtonBase({ children, onClick, ...rest }) {
  return (
    <Button
      variant="text"
      onClick={onClick}
      sx={{ minWidth: 0, px: 1, color: 'text.secondary' }}
      {...rest}
    >
      {children}
    </Button>
  );
}

export default function PaymentsPage() {
  return (
    <Box component="main" sx={{ background: (theme) => theme.palette.background.default }}>
      <Box sx={{ py: { xs: 6, md: 10 }, background: 'linear-gradient(135deg,#0f172a,#1e3a8a)' }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Pay your rent & fees
          </Typography>
          <Typography variant="h3" fontWeight={800} color="common.white" gutterBottom>
            Secure Bank Payments
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Transfer rent, deposits, or management fees directly to our client account. Keep a copy
            of your transfer confirmation for your records.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
              <Typography variant="overline" color="text.secondary">
                Account details
              </Typography>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Bank transfer information
              </Typography>
              <Stack spacing={3}>
                <CopyableDetail label="Account name" value={BANK_DETAILS.accountName} />
                <CopyableDetail label="Sort code" value={BANK_DETAILS.sortCode} />
                <CopyableDetail label="Account number" value={BANK_DETAILS.accountNumber} />
              </Stack>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body2" color="text.secondary">
                Check the account name, sort code and account number carefully before sending funds.
                International senders should contact us for IBAN/SWIFT details first.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper elevation={1} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, height: '100%' }}>
              <Typography variant="overline" color="text.secondary">
                Preferred contact
              </Typography>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Need help with a payment?
              </Typography>
              <Stack spacing={1.5} mb={3}>
                <ContactRow icon={<EmailOutlinedIcon />} label="Email" value={PAYMENT_CONTACT.email} />
                <ContactRow icon={<PhoneOutlinedIcon />} label="Phone" value={PAYMENT_CONTACT.phone} />
                <ContactRow icon={<PlaceOutlinedIcon />} label="Location" value={PAYMENT_CONTACT.location} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Contact: {PAYMENT_CONTACT.name}
                </Typography>
              </Stack>
              <Button
                component="a"
                href={`mailto:${PAYMENT_CONTACT.email}`}
                variant="contained"
                fullWidth
              >
                Email payment team
              </Button>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: 6,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Payment checklist
          </Typography>
          <List>
            {instructions.map((item) => (
              <ListItem key={item} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircleIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Container>
    </Box>
  );
}

function ContactRow({ icon, label, value }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: (theme) => theme.palette.action.hover,
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" textTransform="uppercase">
          {label}
        </Typography>
        <Typography variant="subtitle1" fontWeight={600}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}
